import { createElement, Fragment, useMemo } from "react";
import type { ReactNode } from "react";

type HighlightedTextProps = {
    text: string;
    highlights: Record<string, string>;
    // Type of element to wrap the highlighted text in
    wrapElementType?: keyof JSX.IntrinsicElements;
};

/**
 * HighlightedText replaces template placeholders in a string (e.g. `{{name}}`)
 * with values from the `highlights` object and wraps them in a specified element.
 *
 * @component
 * @param text - The text containing template placeholders (e.g. "Hello, {{name}}!")
 * @param highlights - An object mapping placeholder names to replacement values (e.g. { name: "Alice" })
 * @param wrapElementType- The HTML element to wrap highlighted text (default: "mark")
 *
 * @example
 * // Renders: Welcome, <strong>Bob</strong>!
 * <HighlightedText
 *   text="Welcome, {{user}}!"
 *   highlights={{ user: "Bob" }}
 *   wrapElementType="strong"
 * />
 */
export const HighlightedText: React.FC<HighlightedTextProps> = ({
    text,
    highlights,
    wrapElementType = "mark",
}) => {
    const WrapElement = wrapElementType;
    const textWithHighlights = useMemo(() => {
        const textParts: (string | ReactNode)[] = [];
        let remainingText = text;
        const regex = new RegExp(/({{[a-zA-Z]+}})/, "g");
        const matches = text.match(regex);
        if (!matches) return [text]; // If no matches, return the original text

        matches.map((match) => {
            // Push the first part up until the replacement
            const part = remainingText.split(match);
            textParts.push(part[0]);

            const replacementText = highlights[match.replace(/{{|}}/g, "")];
            // If there's a replacement, push it to the textParts as a mark
            if (replacementText) {
                textParts.push(
                    createElement(WrapElement, {}, replacementText) // create a new element based on wrapElementType
                );
            }

            remainingText = part?.[1] ?? "";
        });
        return textParts;
    }, [text, highlights]);

    if (!highlights) return <>{text}</>;

    return (
        <>
            {textWithHighlights.map((part, index) => (
                <Fragment key={index}>{part}</Fragment>
            ))}
        </>
    );
};
